// React
import React, { useState } from "react";

// i18n
import { useTranslation } from "react-i18next";

// Redux
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// Resources
import { getPatientsByUserEmail } from "API/Resource/patient";

// Components
import {
  Box,
  IconButton,
  Typography,
  Container,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid2";
import { CancelRounded, SearchRounded } from "@mui/icons-material";
import { PatientDrawer } from "Components/PatientDrawer";

// Styles
import { styles } from "../Common/styles";

// Formik
import { Formik } from "formik";

// Validation
import { emailValidationSchema } from "Validation/UserValidation";

const UserManagement = () => {
  const [userList, setUserList] = useState([]);
  const [loadTable, setLoadTable] = useState(false);
  const [loading, setLoading] = useState(false);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const columns = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 170,
      flex: 1,
      valueGetter: (_, row) => `${row.firstName || ""} ${row.lastName || ""}`,
    },
    {
      field: "email",
      headerName: "Patient Email",
      minWidth: 170,
      flex: 1,
    },
    {
      field: "primaryPhone",
      headerName: "Phone",
      width: 150,
    },
    {
      field: "dob",
      headerName: "DOB",
      width: 150,
    },
  ];

  const handleCancel = (resetForm) => {
    resetForm();
    setUserList([]);
    setLoadTable(false);
  };

  const handleUserSearch = async (values) => {
    setUserList([]);
    setLoadTable(true);
    setLoading(true);

    try {
      const { patients } = await getPatientsByUserEmail(values.email, {
        hidden: false,
      });

      patients.sort((a, b) => {
        return (
          a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase()) ||
          a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase())
        );
      });
      setUserList(patients);
    } catch (error) {
      dispatch(openSnackbar({ message: error.message, severity: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const toggleDrawer = (newOpen) => {
    setOpenDrawer(newOpen);
  };

  const handleRowClick = (params) => {
    setSelectedPatient(params.row);
    setOpenDrawer(true);
  };

  return (
    <Container>
      <Box sx={{ ...styles.titleBox, pb: "1rem" }}>
        <Typography sx={styles.title}>User Management</Typography>
      </Box>
      <Container sx={{ ...styles.contentBox, pb: "1.5rem" }}>
        <Formik
          initialValues={{ email: "" }}
          validationSchema={emailValidationSchema(t)}
          onSubmit={handleUserSearch}
        >
          {({
            values,
            handleChange,
            handleBlur,
            errors,
            touched,
            handleSubmit,
            resetForm,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid
                direction={"row"}
                container
                spacing="1rem"
                sx={styles.searchGrid}
              >
                <TextField
                  id="email"
                  name="email"
                  label="Search User"
                  type="email"
                  autoComplete="email"
                  placeholder="Search Patient by User Email"
                  variant="outlined"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="Clear search bar"
                            onClick={() => {
                              handleCancel(resetForm);
                            }}
                            onMouseDown={() => {
                              handleCancel(resetForm);
                            }}
                            edge="end"
                            size="small"
                          >
                            <CancelRounded fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    flexGrow: 1,
                    height: "3rem",
                    "& .MuiInputBase-root": {
                      height: "100%",
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SearchRounded />}
                >
                  Search
                </Button>
              </Grid>
            </form>
          )}
        </Formik>
        {loadTable && (
          <DataGrid
            rows={userList}
            columns={columns}
            onRowClick={handleRowClick}
            rowHeight={60}
            autoHeight
            disableExtendRowFullWidth
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            localeText={{
              noRowsLabel: "No users found under the email provided",
            }}
            pageSizeOptions={[10]}
            loading={loading}
            sx={styles.datagrid}
          />
        )}
        <PatientDrawer
          open={openDrawer}
          toggleDrawer={toggleDrawer}
          patient={selectedPatient}
          setPatients={setUserList}
          onPatientUpdated={setSelectedPatient}
        />
      </Container>
    </Container>
  );
};

export default UserManagement;
